import { v4 as uuidv4 } from "uuid";
import {
  collection,
  doc,
  getDocs,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import db from "../../Firestore/FirestoreConfig";
import CoachNutritionPlan, {
  CoachNutritionPlanMeal,
} from "../../Firestore/nutrition/CoachNutritionPlan";
import {
  buildNutritionPlanDocPath,
  buildNutritionPlanMealDocPath,
  buildNutritionRootDocPath,
  buildPendingClientNutritionPlanDocPath,
  buildPendingClientNutritionPlanMealsCollectionPath,
} from "../../Firestore/nutrition/nutritionPaths";
import { buildCoachNutritionPlanLibraryExportDraft } from "./nutritionPlanLibraryExport";
import { getNutritionPlanDisplayNameFromSources } from "./nutritionPlanNameUtils";

const normalizeId = (value) => String(value ?? "").trim().toLowerCase();

const sortMealsByOrder = (meals = []) =>
  [...(Array.isArray(meals) ? meals : [])].sort(
    (leftMeal, rightMeal) =>
      Number(leftMeal?.order ?? 0) - Number(rightMeal?.order ?? 0),
  );

export const shouldDisablePendingNutritionMenuRowInteractions = ({
  isOffline = false,
  isActionInFlight = false,
} = {}) => isOffline || isActionInFlight;

function buildCoachPlanWritePayload(planDraft = {}, targetPlanId) {
  const normalizedTargetPlanId = normalizeId(targetPlanId);
  if (!normalizedTargetPlanId) {
    return null;
  }

  const mealDocs = sortMealsByOrder(planDraft?.meals).map((mealRecord) =>
    new CoachNutritionPlanMeal(mealRecord).toMealDoc(),
  );
  const plan = new CoachNutritionPlan({
    ...planDraft,
    id: normalizedTargetPlanId,
    meals: mealDocs,
  });
  const summaryTotals = plan.totals;
  const mealCount = mealDocs.length;
  const localTimestamp = new Date();
  const rootDoc = plan.toPlanDoc({
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    rev: 1,
    mealCount,
    summaryTotals,
  });
  const localRootDoc = plan.toPlanDoc({
    createdAt: localTimestamp,
    updatedAt: localTimestamp,
    rev: 1,
    mealCount,
    summaryTotals,
  });

  return {
    rootDoc,
    localRootDoc,
    mealDocs,
  };
}

export async function movePendingNutritionPlanToLibraryTransactional({
  coachId,
  pendingUserId,
  planId,
  fallbackPlanRecord = null,
  language = "en",
  showToast,
} = {}) {
  const normalizedCoachId = String(coachId ?? "").trim();
  const normalizedPendingUserId = String(pendingUserId ?? "").trim();
  const normalizedPlanId = normalizeId(planId);

  if (!normalizedCoachId || !normalizedPendingUserId || !normalizedPlanId) {
    return { ok: false, reason: "invalid_ids" };
  }

  const sourcePlanDocRef = doc(
    db,
    ...buildPendingClientNutritionPlanDocPath(
      normalizedCoachId,
      normalizedPendingUserId,
      normalizedPlanId,
    ),
  );
  const sourceMealsColRef = collection(
    db,
    ...buildPendingClientNutritionPlanMealsCollectionPath(
      normalizedCoachId,
      normalizedPendingUserId,
      normalizedPlanId,
    ),
  );
  const targetPlanId = normalizeId(uuidv4());
  const targetRootDocRef = doc(db, ...buildNutritionRootDocPath(normalizedCoachId));
  const targetPlanDocRef = doc(
    db,
    ...buildNutritionPlanDocPath(normalizedCoachId, targetPlanId),
  );

  try {
    const sourceMealDiscoverySnapshot = await getDocs(sourceMealsColRef);

    let movedPlanDoc = null;
    let movedMealDocs = [];
    await runTransaction(db, async (transaction) => {
      const sourcePlanSnapshot = await transaction.get(sourcePlanDocRef);
      if (!sourcePlanSnapshot.exists()) {
        throw new Error("pending_nutrition_plan_source_missing");
      }

      const sourceMealSnapshots = await Promise.all(
        sourceMealDiscoverySnapshot.docs.map((mealDoc) => transaction.get(mealDoc.ref)),
      );
      const sourceMealRecords = sortMealsByOrder(
        sourceMealSnapshots
          .filter((mealSnapshot) => mealSnapshot.exists())
          .map((mealSnapshot) => ({
            id: mealSnapshot.id,
            ...mealSnapshot.data(),
          })),
      );
      const sourcePlanRecord = {
        id: normalizedPlanId,
        ...sourcePlanSnapshot.data(),
        meals: sourceMealRecords,
      };
      const exportDraft = buildCoachNutritionPlanLibraryExportDraft({
        sourcePlan: sourcePlanRecord,
        exportPlanId: targetPlanId,
        exportName: getNutritionPlanDisplayNameFromSources(
          [sourcePlanRecord, fallbackPlanRecord],
          language,
        ),
      });

      if (!exportDraft) {
        throw new Error("pending_nutrition_plan_export_invalid");
      }

      const writePayload = buildCoachPlanWritePayload(exportDraft, targetPlanId);
      if (!writePayload) {
        throw new Error("pending_nutrition_plan_write_invalid");
      }

      movedPlanDoc = writePayload.localRootDoc;
      movedMealDocs = writePayload.mealDocs;

      transaction.set(
        targetRootDocRef,
        {
          schema_version: "nutrition_v1",
          storage_scope: "coach_only",
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      transaction.set(targetPlanDocRef, writePayload.rootDoc);
      writePayload.mealDocs.forEach((mealDoc) => {
        transaction.set(
          doc(
            db,
            ...buildNutritionPlanMealDocPath(
              normalizedCoachId,
              targetPlanId,
              mealDoc.id,
            ),
          ),
          mealDoc,
        );
      });

      sourceMealSnapshots
        .filter((mealSnapshot) => mealSnapshot.exists())
        .forEach((mealSnapshot) => {
          transaction.delete(mealSnapshot.ref);
        });
      transaction.delete(sourcePlanDocRef);
    });

    if (typeof showToast === "function") {
      showToast("nutritionPlanExportedToLibrary-string", {
        type: "info",
        description: "nutritionPlanExportedToLibraryDescription-string",
        variables: {
          planName: getNutritionPlanDisplayNameFromSources(
            [movedPlanDoc, fallbackPlanRecord],
            language,
          ),
        },
      });
    }

    return {
      ok: true,
      movedPlanId: targetPlanId,
      movedPlanData: movedPlanDoc,
      movedMealData: movedMealDocs,
    };
  } catch (error) {
    console.error("[pending-nutrition-plan-move] transaction_failed", {
      coachId: normalizedCoachId,
      pendingUserId: normalizedPendingUserId,
      planId: normalizedPlanId,
      error: error?.message ?? error,
    });

    return {
      ok: false,
      reason: "transaction_failed",
      error,
    };
  }
}

export async function deletePendingNutritionPlanTransactional({
  coachId,
  pendingUserId,
  planId,
} = {}) {
  const normalizedCoachId = String(coachId ?? "").trim();
  const normalizedPendingUserId = String(pendingUserId ?? "").trim();
  const normalizedPlanId = normalizeId(planId);

  if (!normalizedCoachId || !normalizedPendingUserId || !normalizedPlanId) {
    return { ok: false, reason: "invalid_ids" };
  }

  const sourcePlanDocRef = doc(
    db,
    ...buildPendingClientNutritionPlanDocPath(
      normalizedCoachId,
      normalizedPendingUserId,
      normalizedPlanId,
    ),
  );
  const sourceMealsColRef = collection(
    db,
    ...buildPendingClientNutritionPlanMealsCollectionPath(
      normalizedCoachId,
      normalizedPendingUserId,
      normalizedPlanId,
    ),
  );

  try {
    const sourceMealDiscoverySnapshot = await getDocs(sourceMealsColRef);
    let didDelete = false;

    await runTransaction(db, async (transaction) => {
      const sourcePlanSnapshot = await transaction.get(sourcePlanDocRef);
      if (!sourcePlanSnapshot.exists()) {
        return;
      }

      didDelete = true;
      sourceMealDiscoverySnapshot.docs.forEach((mealDoc) => {
        transaction.delete(mealDoc.ref);
      });
      transaction.delete(sourcePlanDocRef);
    });

    return {
      ok: true,
      didDelete,
    };
  } catch (error) {
    console.error("[pending-nutrition-plan-delete] transaction_failed", {
      coachId: normalizedCoachId,
      pendingUserId: normalizedPendingUserId,
      planId: normalizedPlanId,
      error: error?.message ?? error,
    });

    return {
      ok: false,
      reason: "transaction_failed",
      error,
    };
  }
}
